﻿<script lang="ts" setup>
    import { reactive } from 'vue';
    import { type Consent, type Contact, anonymizeContact } from './Contact.access';
    import ConsentList from '@shared/components/List.vue';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { bsAlert } from '@shared/components/bsAlerts';
    import Loading from '@shared/components/Loading.vue';

    const props = defineProps<{
        consents: Consent[];
        contact: Contact;
    }>();

    const state = reactive<{
        anonymizeStep: 1 | 2;
        isBusy: boolean;
    }>({
        anonymizeStep: 1,
        isBusy: false,
    });

    const emit = defineEmits(['anonymized']);

    const filterOptions = () => {
        return ['[Name]', 'ConsentTemplate.Text'];
    };

    const onAnonymize = () => {
        if (state.isBusy) return;
        state.isBusy = true;

        anonymizeContact(props.contact.ContactId)
            .then((data) => {
                emit('anonymized');
                bsAlert({ title: $Resources.AnonymizationNotificationHeader, content: $Resources.AnonymizationNotificationSuccess });
            })
            .catch(() => {
                bsAlert({ type: 'danger', title: $Resources.AnonymizationNotificationHeader, content: $Resources.AnonymizationNotificationFail });
            })
            .finally(() => {
                state.isBusy = false;
            });
    };
</script>

<script lang="ts"></script>

<template>
    <h2 class="h4">{{ $Resources.ContactModalConsentTab }}</h2>

    <ConsentList itemId="ConsentId" :items="props.consents" :filterKeys="filterOptions()">
        <template #item="{ item, index }">
            <div class="flex-1">
                <blockquote class="answer">
                    <p v-html="item.ConsentTemplate.Text"></p>
                </blockquote>

                <p v-show="!item.RevokedAt">
                    <i class="fa fa-square status-ok" aria-hidden="true"></i>
                    {{ $Resources.ConsentGiven }}
                    <time :datetime="item.GivenAt">{{ formatDateTime(item.GivenAt) }}</time>
                </p>

                <p v-show="item.RevokedAt">
                    <i class="fa fa-square status-not-ok" aria-hidden="true"></i>
                    {{ $Resources.ConsentRevoked }}
                    <time :datetime="item.RevokedAt">{{ formatDateTime(item.RevokedAt) }}</time>
                </p>
            </div>
        </template>
    </ConsentList>

    <BsAlert
        v-if="state.anonymizeStep === 1"
        type="warning"
        :title="$Resources.AnonymizeWarningPreHeader"
        :content="$Resources.AnonymizeWarningBody"
        :buttonText="$Resources.AnonymizeButton"
        :dismissible="false"
        @buttonClick="state.anonymizeStep = 2"></BsAlert>

    <BsAlert
        v-if="state.anonymizeStep === 2"
        type="danger"
        :title="$Resources.AnonymizeWarningPreHeader"
        :content="$Resources.ConfirmAnonymizeBody"
        :buttonText="$Resources.AlertConfirmButtonText"
        :dismissible="false"
        @buttonClick="onAnonymize"></BsAlert>

    <Loading :show="state.isBusy" :fixed="true"></Loading>
</template>

<style>
    .answer {
        margin: var(--ug-spacing-sm) 0;
        border-width: 3px;
    }

    .answer p {
        margin: 0;
    }
</style>
